import { useState } from 'react'
import { useParams } from 'react-router'
import dayjs from 'dayjs'
import range from 'lodash/range'

import ExpandAltOutlinedIcon from '@ant-design/icons/ExpandAltOutlined'

import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@components/_mui/Typography'

import { LabsModal } from '@pages/Labs'
import { LabDetailsModal } from '@pages/Labs/Lab'
import { usePatientLabs, usePatientLabsFilters } from '@pages/Labs/PatientLabs'
import StatusChip from '@components/StatusChip'
import Tile from '@components/Tile'

import { Empty, IconCircleButton } from '../ProfileTab.utils'

export const LIMIT = 4

export default function Labs() {
  const { id } = useParams()
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [labId, setLabId] = useState()

  const [{ labsTab, labsExpanded }, updateFilters] = usePatientLabsFilters()
  const { data, isRefreshing, isPending } = usePatientLabs(id, { filter: labsTab, limit: LIMIT })

  const showLoading = isPending
  const showEmpty = !showLoading && data?.length === 0
  const showData = !showEmpty && data?.length > 0

  return (
    <Tile
      title="Labs"
      refreshing={isRefreshing}
      action={
        <IconCircleButton size="small" onClick={() => updateFilters({ labsExpanded: true })}>
          <ExpandAltOutlinedIcon />
        </IconCircleButton>
      }
      sx={{ height: '100%' }}
    >
      <LabDetailsModal labId={labId} open={detailsOpen} onClose={() => setDetailsOpen(false)} />
      <LabsModal patientId={id} open={labsExpanded} onClose={() => updateFilters({ labsExpanded: false })} />
      <Stack>
        <Tabs value={labsTab} onChange={(e, labsTab) => updateFilters({ labsTab })}>
          <Tab label="All" value="all" />
          <Tab label="Complete" value="complete" />
          <Tab label="Pending" value="pending" />
          <Tab label="Follow-up" value="follow-up" />
          <Tab label="Failed" value="failed" />
        </Tabs>
        <Divider />
        {showLoading && <Loading />}
        {showEmpty && <Empty />}
        {showData && (
          <List disablePadding data-testid="labs">
            {data.map((lab, i) => (
              <Item
                key={lab.id}
                divider={i < data.length - 1}
                lab={lab}
                onClick={() => {
                  setLabId(lab.id)
                  setDetailsOpen(true)
                }}
              />
            ))}
          </List>
        )}
      </Stack>
    </Tile>
  )
}

function Item({ divider, lab, onClick }) {
  return (
    <ListItemButton divider={divider} onClick={onClick}>
      <Stack direction="row" sx={{ gap: 2, justifyContent: 'space-between', width: '100%' }}>
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
            <Typography noWrap>{lab.lab}</Typography>
            <StatusChip.Lab status={lab.status} noLabel />
          </Stack>
          <Typography variant="body2">{lab.panelsToString}</Typography>
        </Stack>
        <Stack>
          {lab.resultDate && (
            <Stack direction="row" sx={{ gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Result:
              </Typography>
              <Typography sx={{ fontVariantNumeric: 'tabular-nums' }}>{dayjs(lab.resultDate).format('L')}</Typography>
            </Stack>
          )}
          <Stack direction="row" sx={{ gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Created:
            </Typography>
            <Typography sx={{ fontVariantNumeric: 'tabular-nums' }}>{dayjs(lab.createdAt).format('L')}</Typography>
          </Stack>
        </Stack>
      </Stack>
    </ListItemButton>
  )
}

Item.Loading = function ({ divider }) {
  return (
    <ListItemButton divider={divider}>
      <Stack direction="row" sx={{ gap: 0.5, justifyContent: 'space-between', width: '100%' }}>
        <Typography>
          <Skeleton width={150} />
        </Typography>
        <Stack direction="row" sx={{ gap: 1 }}>
          <Typography>
            <Skeleton width={80} />
          </Typography>
          <Typography>
            <Skeleton width={80} />
          </Typography>
        </Stack>
      </Stack>
    </ListItemButton>
  )
}

function Loading() {
  return range(0, LIMIT).map((i) => <Item.Loading key={i} divider={i < LIMIT - 1} />)
}
